/**
 * 参数配置 - 数据加载与状态编排
 *
 * 数据模型三级结构：栏目（Column）> 模块（Module）> 参数（Param）
 * 加载策略：
 *   - 进入页面：拉栏目列表 → 自动选中第一个 → 拉该栏目下模块 + 各模块参数
 *   - 切换栏目：命中缓存直接渲染；未加载过则按需拉取
 *   - 增删改：成功后局部刷新（不全量重拉），保证 sortOrder / createTime 等字段与后端一致
 *
 * 缓存说明：
 *   - modulesByColumn[columnId]   缓存某栏目下的模块数组
 *   - paramsByModule[moduleId]    缓存某模块下的参数数组
 *   - sourceOptionsCache[type]    缓存某来源类型的候选项（CHANNEL 等）
 *   Map 的 key 全部用 String(id) 转换，避免 TS 类型噪音
 */
import { computed, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { OK, reportEvent } from '@/common'
import {
  addSettingColumn,
  deleteSettingColumn,
  deleteSettingModule,
  deleteSettingParam,
  getSettingColumns,
  getSettingModules,
  getSettingParamSourceOptions,
  getSettingParams,
  saveSettingModule,
  saveSettingParam,
  sortSettingModules,
  type ModuleSavePayload,
  type ParamSavePayload,
} from '@/api/paramSettingApi'
import type { ColumnVO, ModuleVO, ParamVO, SourceOption } from '../types'

/**
 * 通用响应 code 成功判断
 * 兼容后端可能返回数字 0 或字符串 '0'（项目里 OK 常量是 '0'）
 */
const isOk = (code: unknown) => String(code) === OK

/**
 * 通用排序：按 sortOrder 升序；sortOrder 缺失时按 id 升序兜底
 * 兼顾"后端未返回 sortOrder"或"两条数据 sortOrder 相同"的情况
 */
const sortBySortOrder = <T extends { id: number; sortOrder?: number }>(list: T[]): T[] =>
  [...list].sort((a, b) => {
    const sa = a.sortOrder ?? Number.MAX_SAFE_INTEGER
    const sb = b.sortOrder ?? Number.MAX_SAFE_INTEGER
    if (sa !== sb) return sa - sb
    return (a.id ?? 0) - (b.id ?? 0)
  })

const sortColumns = (list: ColumnVO[]): ColumnVO[] => sortBySortOrder(list)
const sortModules = (list: ModuleVO[]): ModuleVO[] => sortBySortOrder(list)

/**
 * 统一查询埋点
 * - eventName: '_search'（项目数据查询类事件名）
 * - ui_name: '参数配置搜索'（与项目其它页面 _search 风格一致：'XX搜索'）
 * - return_duration: 秒级耗时
 * - query_status: 接口业务 code 是否为 0
 */
const reportSearch = (startTime: number, ok: boolean) => {
  reportEvent({
    eventName: '_search',
    properties: {
      ui_name: '参数配置搜索',
      app_name: '',
      return_duration: (new Date().getTime() - startTime) / 1000,
      query_status: ok ? 'succeed' : 'failed',
    },
  })
}

/**
 * 参数配置主 composable
 * 暴露所有数据与变更方法；UI 层组件只关心绑定和事件，不直接调接口
 */
export function useParamSetting() {
  // ===== 源状态 =====
  /** 栏目列表（按 sortOrder 升序） */
  const columns = ref<ColumnVO[]>([])
  /** 当前激活的栏目 id；无栏目时为 null */
  const activeColumnId = ref<number | null>(null)
  /** 模块缓存：key = String(columnId) */
  const modulesByColumn = reactive<Record<string, ModuleVO[]>>({})
  /** 参数缓存：key = String(moduleId) */
  const paramsByModule = reactive<Record<string, ParamVO[]>>({})
  /** 栏目区 loading（拉栏目列表时为 true） */
  const loadingColumns = ref(false)
  /** 主区 loading（拉模块 + 参数时为 true） */
  const loadingContent = ref(false)

  // ===== 派生状态 =====
  /** 当前激活栏目下的模块列表（无栏目时返回 []） */
  const currentModules = computed<ModuleVO[]>(() =>
    activeColumnId.value == null ? [] : modulesByColumn[String(activeColumnId.value)] || [],
  )

  /** 栏目为空的派生标识：loading 结束 + 栏目数为 0 */
  const isColumnsEmpty = computed(() => !loadingColumns.value && columns.value.length === 0)

  // ===== 加载类 =====

  /**
   * 加载栏目列表
   * 触发时机：页面 mounted；新增 / 删除栏目后局部刷新
   * 副作用：若返回非空，会自动 switchColumn 到第一个栏目（触发模块 + 参数加载）
   */
  const loadColumns = async () => {
    loadingColumns.value = true
    const startTime = Date.now()
    let ok = false
    try {
      const res = await getSettingColumns()
      ok = isOk(res?.code)
      if (ok) {
        columns.value = sortColumns(res?.data || [])
        if (columns.value.length) {
          await switchColumn(columns.value[0].id)
        }
      }
    } catch (e) {
      console.error('加载栏目列表失败', e)
    } finally {
      loadingColumns.value = false
      reportSearch(startTime, ok)
    }
  }

  /**
   * 每栏目独立的加载 token（key = String(columnId)）
   * 作用：
   * - 同一栏目的旧请求被新请求作废（防"切走又切回，慢请求覆盖新数据"）
   * - 不同栏目互不干扰（移动模块后并发刷新源/目标两栏目，各写各的 key，不会互相误杀）
   * 相比单一全局 token，天然区分"同 key 竞争"与"跨 key 并发"，无需额外开关。
   */
  const loadTokens = new Map<string, number>()

  /**
   * 加载指定栏目下的模块列表 + 各模块的参数列表
   * 始终调接口拉最新数据；不再使用缓存
   *
   * 竞态处理（per-column token）：进入时对该栏目 ++token，异步回调里比对——
   * 若该栏目已有更新的加载发起（token 变化），本次结果全部丢弃、不写缓存，
   * 只保留该栏目最新一次请求的结果。不同栏目 token 独立，互不影响。
   *
   * loading 语义：loadingContent 仅服务于"当前激活栏目"的加载。
   * 后台刷新非激活栏目（如移动模块后刷新源/目标栏目）不显示主区 loading，
   * 也不会误关正在服务于当前栏目切换的 loading。
   */
  const loadModulesAndParams = async (columnId: number) => {
    const key = String(columnId)
    const myToken = (loadTokens.get(key) ?? 0) + 1
    loadTokens.set(key, myToken)
    // 本次结果是否已被同栏目更新的请求作废
    const isStale = () => loadTokens.get(key) !== myToken
    // 是否应驱动主区 loading：仅当加载的就是当前激活栏目
    const drivesLoading = () => activeColumnId.value === columnId

    if (drivesLoading()) loadingContent.value = true
    const startTime = Date.now()
    let ok = false
    try {
      const res = await getSettingModules(columnId)
      // 已被同栏目新的加载覆盖 → 丢弃本次结果，不写状态
      if (isStale()) return
      ok = isOk(res?.code)
      if (!ok) return
      const list: ModuleVO[] = sortModules(res?.data || [])
      modulesByColumn[key] = list

      // 并发拉取每个模块的参数；每个回调里再次检查是否已过期
      await Promise.all(
        list.map(async (module) => {
          const mkey = String(module.id)
          try {
            const paramRes = await getSettingParams(module.id)
            if (isStale()) return
            paramsByModule[mkey] = isOk(paramRes?.code) ? paramRes?.data || [] : []
          } catch (e) {
            if (isStale()) return
            console.error(`加载模块 ${module.id} 的参数失败`, e)
            paramsByModule[mkey] = []
          }
        }),
      )
    } catch (e) {
      if (isStale()) return
      console.error('加载模块列表失败', e)
      message.error('加载模块失败，请稍后重试')
    } finally {
      // 仅当本次仍是该栏目最新请求、且加载的就是当前激活栏目时才关 loading，
      // 避免后台刷新或旧请求误关正在进行的当前栏目 loading
      if (!isStale() && drivesLoading()) {
        loadingContent.value = false
      }
      reportSearch(startTime, ok)
    }
  }

  /**
   * 切换栏目
   * 1) 立即更新 activeColumnId（Tab 高亮立即跟手）
   * 2) 清空旧栏目的模块/参数缓存？不需要 — 锁机制会保证新数据覆盖
   * 3) loadModulesAndParams 内部用 token 防竞态：
   *    用户连续快速切换 A → B → C，A/B 的接口即使在 C 之后才返回，
   *    也会被 token 检查丢弃，最终只保留 C 的数据
   */
  const switchColumn = async (columnId: number) => {
    activeColumnId.value = columnId
    await loadModulesAndParams(columnId)
  }

  /**
   * 强制重新加载指定栏目的模块 + 参数
   * 用于：新增/编辑/删除/移动模块成功后，需要刷新最新字段
   * per-column token 保证：并发刷新不同栏目（如移动模块后刷新源/目标两栏目）各写各的 key，
   * 互不误杀；刷新非激活栏目时静默进行、不显示主区 loading
   */
  const reloadColumn = async (columnId: number) => {
    await loadModulesAndParams(columnId)
  }

  // ===== 栏目 CRUD =====

  /**
   * 新增栏目
   * 表单非空校验已在弹窗组件层完成；这里只做兜底 trim
   * 成功后重拉栏目列表，并自动切到新栏目（依赖后端返回的 id）
   * @returns Promise<boolean> true 表示成功，false 用于让弹窗保持打开
   */
  const createColumn = async (columnName: string): Promise<boolean> => {
    const name = (columnName || '').trim()
    if (!name) return false
    try {
      const res = await addSettingColumn(name)
      if (!isOk(res?.code)) return false
      const newId: number | undefined = res?.data?.id
      // 重新拉取栏目列表，保证 sortOrder / createTime 等字段与后端一致
      await loadColumns()
      // 默认切到新建的栏目（若后端返回了 id 且能在最新列表中找到）
      if (typeof newId === 'number' && columns.value.some((c) => c.id === newId)) {
        await switchColumn(newId)
      }
      message.success('栏目新增成功')
      return true
    } catch (e) {
      console.error('新增栏目失败', e)
      return false
    }
  }

  /**
   * 删除栏目
   * 后端校验：若栏目下还有模块（del_flag=0），会拒绝并返回 msg：「请先删除栏目下所有模块」
   * 该提示由全局 request 拦截器统一 toast，前端无需额外处理
   *
   * 删除当前激活栏目时：自动切到前一个；若被删的是首位，切到新首位；全空则置 null
   */
  const removeColumn = async (columnId: number): Promise<boolean> => {
    try {
      const res = await deleteSettingColumn(columnId)
      if (!isOk(res?.code)) return false
      const key = String(columnId)
      const removedIndex = columns.value.findIndex((c) => c.id === columnId)
      columns.value = columns.value.filter((c) => c.id !== columnId)
      delete modulesByColumn[key]
      if (activeColumnId.value === columnId) {
        if (columns.value.length === 0) {
          activeColumnId.value = null
        } else {
          // 切到前一个，若删的是 index 0 则保留切到新首位
          const fallbackIndex = Math.max(0, removedIndex - 1)
          await switchColumn(columns.value[fallbackIndex].id)
        }
      }
      message.success('栏目删除成功')
      return true
    } catch (e) {
      console.error('删除栏目失败', e)
      return false
    }
  }

  // ===== 模块 CRUD =====

  /**
   * 新增 / 编辑模块（统一走 save 接口，id 为 null 表示新增）
   * 成功后 reloadColumn 触发该栏目的模块 + 参数重拉，保证字段一致
   * @param payload.id          null=新增；非空=编辑
   * @param payload.columnId    所属栏目 id
   * @param payload.moduleName  模块名称（前置校验：非空）
   * @param payload.sortOrder   排序位（新增时取当前模块数 = 追加到末尾）
   */
  const saveModule = async (payload: ModuleSavePayload): Promise<boolean> => {
    const name = (payload.moduleName || '').trim()
    const code = (payload.moduleCode || '').trim()
    // 表单校验已在弹窗组件层完成；这里只做兜底，避免直接调用时传脏数据
    if (!name || !code) return false
    try {
      const res = await saveSettingModule({ ...payload, moduleName: name, moduleCode: code })
      if (!isOk(res?.code)) return false
      message.success(payload.id == null ? '模块新增成功' : '模块更新成功')
      await reloadColumn(payload.columnId)
      return true
    } catch (e) {
      console.error('保存模块失败', e)
      return false
    }
  }

  /**
   * 删除模块
   * 后端校验：若模块下还有参数，会拒绝并返回「请先删除模块下所有参数」（全局拦截 toast）
   * 前端做局部剔除（不全量重拉），同时清掉该模块的参数缓存
   */
  const removeModule = async (moduleId: number, columnId: number): Promise<boolean> => {
    try {
      const res = await deleteSettingModule(moduleId)
      if (!isOk(res?.code)) return false
      const key = String(columnId)
      if (modulesByColumn[key]) {
        modulesByColumn[key] = modulesByColumn[key].filter((m) => m.id !== moduleId)
      }
      delete paramsByModule[String(moduleId)]
      message.success('模块删除成功')
      return true
    } catch (e) {
      console.error('删除模块失败', e)
      return false
    }
  }

  /**
   * 切换模块所属栏目
   * 复用 /setting/module/save 编辑接口，columnId 改为目标栏目 id 即完成迁移
   * 同 columnId 直接返回 true 不发请求
   * 成功后：源栏目 + 目标栏目（已加载过的）都需重拉，确保 sortOrder 等字段一致
   */
  const moveModule = async (moduleId: number, fromColumnId: number, targetColumnId: number): Promise<boolean> => {
    if (fromColumnId === targetColumnId) return true
    // 从源栏目缓存里取出原模块完整数据，save 接口需要 moduleName / moduleCode
    const source = modulesByColumn[String(fromColumnId)]?.find((m) => m.id === moduleId)
    if (!source) {
      console.error('移动模块失败：未找到源模块数据', moduleId)
      return false
    }
    try {
      // 先确保目标栏目数据已加载，才能算出正确的末尾 sortOrder
      // 用 reloadColumn（per-column token，不会被切换作废），保证目标缓存一定写入
      const targetKey = String(targetColumnId)
      if (!modulesByColumn[targetKey]) {
        await reloadColumn(targetColumnId)
      }
      // 追加到目标栏目末尾：取现有最大 sortOrder + 1（空栏目则为 0）
      // 不用 length，避免目标栏目 sortOrder 非 0..n-1 连续时算错插入位
      const targetList = modulesByColumn[targetKey] || []
      const nextSortOrder = targetList.length
        ? Math.max(...targetList.map((m) => m.sortOrder ?? 0)) + 1
        : 0
      const res = await saveSettingModule({
        id: moduleId,
        columnId: targetColumnId,
        moduleName: source.moduleName,
        moduleCode: source.moduleCode,
        sortOrder: nextSortOrder,
      })
      if (!isOk(res?.code)) return false
      message.success('模块已移动到目标栏目')
      await Promise.all([reloadColumn(fromColumnId), reloadColumn(targetColumnId)])
      return true
    } catch (e) {
      console.error('移动模块失败', e)
      return false
    }
  }

  /**
   * 拖拽排序后批量更新
   * 策略：先乐观更新前端顺序立即反馈 → 调批量排序接口 → 失败回滚
   * @param columnId    当前栏目 id
   * @param orderedIds  拖拽后该栏目下模块 id 的最新顺序（前端按此顺序生成 sortList）
   */
  const reorderModules = async (columnId: number, orderedIds: number[]): Promise<boolean> => {
    const key = String(columnId)
    const list = modulesByColumn[key]
    if (!list) return false

    // 1) 乐观更新：按 orderedIds 重排前端缓存，立即响应 UI
    const map = new Map(list.map((m) => [m.id, m]))
    const reordered: ModuleVO[] = []
    orderedIds.forEach((id, idx) => {
      const m = map.get(id)
      if (m) reordered.push({ ...m, sortOrder: idx })
    })
    // 顺序数组与原列表长度不一致表示数据异常，直接放弃
    if (reordered.length !== list.length) return false
    modulesByColumn[key] = reordered

    try {
      // 2) 把最新顺序按 [{id, sortOrder}] 一次性提交给后端
      const sortList = orderedIds.map((id, idx) => ({ id, sortOrder: idx }))
      const res = await sortSettingModules(columnId, sortList)
      if (!isOk(res?.code)) {
        // 3) 失败回滚到原来按 sortOrder 排序的状态
        modulesByColumn[key] = sortModules(list)
        return false
      }
      // 4) 成功后重拉当前栏目，确保 sortOrder / 其它字段与后端一致
      await reloadColumn(columnId)
      return true
    } catch (e) {
      console.error('模块排序失败', e)
      modulesByColumn[key] = sortModules(list)
      return false
    }
  }

  // ===== 参数 CRUD =====

  /**
   * 刷新单个模块的参数列表
   * 用于：新增 / 编辑参数成功后，只重拉这一个模块，避免整栏目重拉浪费请求
   */
  const reloadModuleParams = async (moduleId: number) => {
    const startTime = Date.now()
    let ok = false
    try {
      const res = await getSettingParams(moduleId)
      ok = isOk(res?.code)
      paramsByModule[String(moduleId)] = ok ? res?.data || [] : []
    } catch (e) {
      console.error(`重新加载模块 ${moduleId} 参数失败`, e)
    } finally {
      reportSearch(startTime, ok)
    }
  }

  /**
   * 新增 / 编辑参数（统一走 save 接口，id 为 null 表示新增）
   * 表单校验已在弹窗组件层完成（名称 / 编码 / 值非空），此处只做兜底 trim
   * 成功后只重拉该模块的参数列表
   */
  const saveParam = async (payload: ParamSavePayload): Promise<boolean> => {
    const name = (payload.paramName || '').trim()
    const code = (payload.paramCode || '').trim()
    if (!name || !code) return false
    try {
      const res = await saveSettingParam({ ...payload, paramName: name, paramCode: code })
      if (!isOk(res?.code)) return false
      message.success(payload.id == null ? '参数新增成功' : '参数更新成功')
      await reloadModuleParams(payload.moduleId)
      return true
    } catch (e) {
      console.error('保存参数失败', e)
      return false
    }
  }

  /**
   * 删除参数
   * 前端局部剔除，不重拉
   */
  const removeParam = async (paramId: number, moduleId: number): Promise<boolean> => {
    try {
      const res = await deleteSettingParam(paramId)
      if (!isOk(res?.code)) return false
      const list = paramsByModule[String(moduleId)]
      if (list) {
        paramsByModule[String(moduleId)] = list.filter((p) => p.id !== paramId)
      }
      message.success('参数删除成功')
      return true
    } catch (e) {
      console.error('删除参数失败', e)
      return false
    }
  }

  // ===== 参数来源候选项 =====

  /**
   * APP / CHANNEL 沿用来源缓存；CONTACTS 每次按当前身份和条件重新查询。
   */
  const sourceOptionsCache = reactive<Record<string, SourceOption[]>>({})

  /**
   * 拉取来源候选项；CONTACTS 绕过缓存并向调用方传播失败。
   * @param sourceType 'CHANNEL' | 'CONTACTS' | 'APP'
   */
  const fetchSourceOptions = async (sourceType: string, keyword = ''): Promise<SourceOption[]> => {
    if (sourceType !== 'CONTACTS' && sourceOptionsCache[sourceType]) return sourceOptionsCache[sourceType]
    const startTime = Date.now()
    let ok = false
    try {
      const res = await getSettingParamSourceOptions(sourceType === 'CONTACTS' ? { sourceType, keyword } : { sourceType })
      ok = isOk(res?.code)
      if (ok) {
        const list: SourceOption[] = res?.data?.list || []
        if (sourceType !== 'CONTACTS') sourceOptionsCache[sourceType] = list
        return list
      }
      if (sourceType === 'CONTACTS') throw new Error('人员查询失败，请重试')
    } catch (e) {
      if (sourceType === 'CONTACTS') throw e
      console.error(`加载 ${sourceType} 候选项失败`, e)
    } finally {
      reportSearch(startTime, ok)
    }
    return []
  }

  return {
    // 数据
    columns,
    activeColumnId,
    modulesByColumn,
    paramsByModule,
    currentModules,
    isColumnsEmpty,
    loadingColumns,
    loadingContent,
    // 加载
    loadColumns,
    switchColumn,
    // 栏目
    createColumn,
    removeColumn,
    // 模块
    saveModule,
    removeModule,
    moveModule,
    reorderModules,
    // 参数
    saveParam,
    removeParam,
    // 来源候选项
    fetchSourceOptions,
  }
}
